import { ArrowRight, Sparkles, Activity, BarChart3, Code2 } from "lucide-react";

const HERO_PHOTO =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=80";

const Mockups = () => (
  <div className="relative w-full h-[440px] sm:h-[480px] lg:h-[560px]" aria-hidden="true">
    {/* Background photo card — developer dashboard */}
    <div
      className="photo-frame absolute inset-x-2 sm:inset-x-0 top-0 h-[280px] sm:h-[320px] surface-strong rounded-2xl !p-0 animate-float-slow"
      style={{ "--r": "-2deg" }}
    >
      <img src={HERO_PHOTO} alt="Developer analytics" loading="lazy" />
      <div className="photo-tag">
        <Code2 className="inline w-3 h-3 mr-1.5" />
        Live Dashboard
      </div>
      <div className="absolute bottom-3 left-3 right-3 z-[2] flex items-center justify-between text-white">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-80 font-mono-d">Active Now</div>
          <div className="font-display text-2xl font-bold">2,841</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-80 font-mono-d">Latency</div>
          <div className="font-display text-2xl font-bold">42<span className="text-sm">ms</span></div>
        </div>
      </div>
    </div>

    {/* Floating stat tile */}
    <div
      className="absolute right-2 sm:right-4 bottom-20 sm:bottom-24 w-[230px] sm:w-[250px] surface-strong rounded-2xl p-4 animate-float"
      style={{ "--r": "2deg", animationDelay: "0.6s" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-[#10b981]" />
        <span className="text-xs text-[#6b7280] font-medium">Performance</span>
      </div>
      {[
        { l: "Lighthouse", v: 99 },
        { l: "Uptime", v: 100 },
        { l: "Build", v: 94 },
      ].map((m) => (
        <div key={m.l} className="mb-2">
          <div className="flex justify-between text-[11px] text-[#475569] mb-1">
            <span>{m.l}</span>
            <span className="font-mono-d text-[#0a0e1a] font-semibold">{m.v}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-[#eef1f6] overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#0066ff] to-[#10b981]"
              style={{ width: `${m.v}%` }}
            />
          </div>
        </div>
      ))}
    </div>

    {/* Live activity tile */}
    <div
      className="absolute left-2 sm:left-0 bottom-0 w-[220px] sm:w-[240px] surface-strong rounded-2xl p-4 animate-float"
      style={{ "--r": "-2deg", animationDelay: "1.4s" }}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-[#6b7280] font-medium">Deployments</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10b981]/12 text-[#10b981] font-bold tracking-wide font-mono-d">
          +24%
        </span>
      </div>
      <div className="font-display text-2xl font-bold text-[#0a0e1a]">128</div>
      <div className="text-[#10b981] text-xs flex items-center gap-1 mt-1">
        <Activity className="w-3 h-3" /> 42 today
      </div>
      <div className="mt-3 h-10 flex items-end gap-0.5">
        {[35, 50, 38, 70, 60, 82, 72, 95, 78, 100, 88, 92, 65, 78].map((h, i) => (
          <div
            key={`b-${i}-${h}`}
            className="flex-1 rounded-sm bg-gradient-to-t from-[#0066ff]/15 to-[#0066ff]"
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  </div>
);

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" data-testid="hero-section" className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 overflow-hidden">
      <div className="absolute inset-0 bg-jaali" aria-hidden="true" />
      <div className="absolute inset-0 bg-radial-glow" aria-hidden="true" />
      <div className="absolute -top-24 -right-32 w-[480px] h-[480px] rounded-full bg-[#0066ff]/8 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-32 w-[480px] h-[480px] rounded-full bg-[#10b981]/8 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 reveal">
            <div className="pill pill-accent mb-7" data-testid="hero-pill">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-[#10b981] pulse-dot" />
                <span className="relative w-2 h-2 rounded-full bg-[#10b981]" />
              </span>
              <span className="font-mono-d uppercase tracking-[0.18em] text-[11px]">Studio • Est. 2024</span>
              <Sparkles className="w-3.5 h-3.5" />
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[88px] font-extrabold leading-[0.95] tracking-tight text-[#0a0e1a]">
              Premium Web &
              <br />
              Mobile <span className="text-grad">Development</span>.
            </h1>

            <p className="mt-7 text-lg sm:text-xl text-[#475569] max-w-xl leading-relaxed">
              We build fast, secure, scalable products — shipped by a senior team.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <button data-testid="hero-cta-start-project" onClick={() => scrollTo("contact")} className="btn-primary">
                Start a Project <ArrowRight className="w-4 h-4" />
              </button>
              <button data-testid="hero-cta-view-work" onClick={() => scrollTo("portfolio")} className="btn-ghost">
                View Our Work
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 reveal" style={{ transitionDelay: "120ms" }}>
            <Mockups />
          </div>
        </div>
      </div>
    </section>
  );
}
